import {
	createContext,
	useContext,
	useEffect,
	useRef,
	ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';
import {io, Socket} from 'socket.io-client';
import {v4 as uuidv4} from 'uuid';

export enum ESharedEvents {
	QuizWinEvent = 'QuizWinEvent',
	SharedConfigUpdateEvent = 'SharedConfigUpdateEvent',
	RequestConfigEvent = 'RequestConfigEvent',
}

interface SharedEventContextProps {
	sendEvent: (event: ESharedEvents, data?: any, forceSend?: boolean) => void;
	subscribe: (
		event: ESharedEvents,
		callback: (data?: any) => void
	) => () => void;
	isInitialized: boolean;
}

interface SharedEventProviderProps {
	children: ReactNode;
}

interface SharedEventData {
	event: ESharedEvents;
	id: string;
	data?: any;
}

const SharedEventContext = createContext<SharedEventContextProps | undefined>(
	undefined
);

const LimodorWheelOfFortuneIdentifier = 'limodor-wheel-of-fortune';

export const SharedEventProvider = ({children}: SharedEventProviderProps) => {
	const broadcastChannel = useRef<BroadcastChannel | undefined>(undefined);
	const socket = useRef<Socket | undefined>(undefined);
	const [isInitialized, setIsInitialized] = useState(false);

	const handledMessages = useRef<string[]>([]);
	const eventCallbacks = useRef<{
		[key in ESharedEvents]?: ((data?: any) => void)[];
	}>({});

	const handleIncommingMessage = useCallback((data: any) => {
		if (typeof data === 'object' && 'event' in data && 'id' in data) {
			const eventData = data as SharedEventData;

			// Check that we didn't handle this message already
			if (handledMessages.current.includes(eventData.id)) {
				console.log(
					`Not handling duplicate incomming event: ${eventData.event} (${eventData.id})`
				);
			} else {
				// Add to handled Messages
				handledMessages.current.push(eventData.id);

				// Make sure handledMMessages does not contain more than 100 items, keeping the last added items
				handledMessages.current = handledMessages.current.slice(-100);

				// Notify all listeners
				const event = eventData.event;
				if (eventCallbacks.current[event]) {
					eventCallbacks.current[event].forEach((callback) => {
						callback(eventData.data);
					});
				}

				console.log(
					`Handled incomming event: ${eventData.event} (${eventData.id})`
				);
			}
		}
	}, []);

	// Connect to Broadcast Channel
	useEffect(() => {
		const channel = new BroadcastChannel(LimodorWheelOfFortuneIdentifier);

		channel.onmessage = ({data}) => {
			handleIncommingMessage(data);
		};

		broadcastChannel.current = channel;

		return () => {
			channel.close();
			broadcastChannel.current = undefined;
		};
	}, [handleIncommingMessage]);

	// Connect to SocketIO
	useEffect(() => {
		const domain = 'https://limodor-wof.server7.dev-web.at/';
		console.log(`Connection to SocketIO on ${domain}`);
		const aSocket = io(domain);

		aSocket.on('connect', () => {
			console.log('Connected to SocketIO');
		});

		aSocket.on('message', (data) => {
			handleIncommingMessage(data);
		});

		socket.current = aSocket;
		setIsInitialized(true);

		return () => {
			aSocket.disconnect();
			socket.current = undefined;
			setIsInitialized(false);
		};
	}, [handleIncommingMessage]);

	const sendEvent = useCallback(
		(
			event: ESharedEvents,
			data: any = undefined,
			forceSend: boolean = false
		) => {
			const id = uuidv4();

			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const sharedEvent = {event, id, data};

			// Send to broadcast Channel
			try {
				broadcastChannel.current?.postMessage(sharedEvent);
			} catch (e) {
				console.error(
					`Error while sending Message to BroadcastChannel`,
					e
				);
			}

			// Send to socketIO
			if (socket.current?.connected || forceSend) {
				socket.current?.emit('message', sharedEvent);
			}
		},
		[]
	);

	const subscribe = useCallback(
		(event: ESharedEvents, callback: () => void) => {
			if (eventCallbacks.current[event] === undefined) {
				eventCallbacks.current[event] = [];
			}
			eventCallbacks.current[event].push(callback);

			const unsubscribe = () => {
				// Remove Callback
				eventCallbacks.current[event] = eventCallbacks.current[
					event
				]?.filter((cb) => cb !== callback);
			};

			return unsubscribe;
		},
		[]
	);

	const value = useMemo(() => {
		return {sendEvent, subscribe, isInitialized};
	}, [isInitialized, sendEvent, subscribe]);

	return (
		<SharedEventContext.Provider value={value}>
			{children}
		</SharedEventContext.Provider>
	);
};

export const useSharedEvent = () => {
	const context = useContext(SharedEventContext);
	if (!context) {
		throw new Error(
			'useSharedEvent must be used within an SharedEventContext'
		);
	}
	return context;
};
